import { AxiosStatusCode } from '../axios';
import * as session from './session';

export interface StoredAccount {
    token: string; // the encrypted login token, same string SetLoginToken stores
    userID: string;
    name: string;
    email: string;
}

const storageKey = 'accounts';

function load(): StoredAccount[] {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const accounts: StoredAccount[] = [];
        for (const item of parsed) {
            if (!item || typeof item !== 'object') {
                continue;
            }
            const record = item as Record<string, unknown>;
            if (typeof record.token !== 'string' ||
                typeof record.userID !== 'string' ||
                typeof record.name !== 'string' ||
                typeof record.email !== 'string') {
                continue;
            }
            accounts.push({
                token: record.token,
                userID: record.userID,
                name: record.name,
                email: record.email,
            });
        }
        return accounts;
    } catch {
        return [];
    }
}

function save(accounts: StoredAccount[]) {
    window.localStorage.setItem(storageKey, JSON.stringify(accounts));
}

export function List(): StoredAccount[] {
    return load();
}

export function Remember(account: StoredAccount) {
    const accounts = load();
    const existing = accounts.findIndex(a => a.userID === account.userID);
    if (existing >= 0) {
        accounts[existing] = {
            ...accounts[existing],
            token: account.token,
            name: account.name,
            email: account.email,
        };
    } else {
        accounts.push(account);
    }
    save(accounts);
}

export function Remove(userID: string) {
    save(load().filter(a => a.userID !== userID));
}

export function IsCurrent(account: StoredAccount): boolean {
    return account.token === session.GetLoginToken();
}

export async function Switch(account: StoredAccount): Promise<void> {
    try {
        // Both tokens come from this browser's own stored accounts, which is
        // exactly the proof the server requires before linking them.
        await session.CreateSessionWithLoginToken(account.token, session.GetLoginToken());
    } catch (err: unknown) {
        if (AxiosStatusCode(err) === 404) {
            Remove(account.userID);
        }
        throw err;
    }
}
